import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { initials } from '@/shared/lib/format';
import { PageHeader } from '@/shared/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { ColorPicker } from '@/shared/ui/color-picker';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  fetchNpsBannerBlob,
  fetchNpsProfileBlob,
  updateNpsAccount,
  updateNpsPreferences,
  uploadNpsBanner,
  uploadNpsProfilePhoto,
  useNpsMe,
} from '../api/use-nps';
import { formatCpfCnpj, stripCpfCnpj } from '../utils/format-cpf-cnpj';
import { ImageUploadCard } from '../components/profile/image-upload-card';
import { PassbookIdField } from '../components/survey/passbook-id-field';

export function NpsProfilePage() {
  const queryClient = useQueryClient();
  const { data: me, isLoading } = useNpsMe();

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [passbookId, setPassbookId] = useState('');
  const [primary, setPrimary] = useState('#2563eb');
  const [secondary, setSecondary] = useState('#7c3aed');
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState<'banner' | 'profile' | 'logo' | null>(null);

  useEffect(() => {
    if (me) {
      setName(me.name ?? '');
      setLastName(me.last_name ?? '');
      setTaxId(me.tax_identifier ? formatCpfCnpj(me.tax_identifier) : '');
      setPassbookId(me.legacy_user_id ?? '');
      setPrimary(me.user_preference?.primary_color ?? '#2563eb');
      setSecondary(me.user_preference?.secondary_color ?? '#7c3aed');
      setBannerUrl(me.banner ?? null);
      setProfileUrl(me.profile ?? null);
    }
  }, [me]);

  useEffect(() => {
    let bannerObjectUrl: string | undefined;
    let profileObjectUrl: string | undefined;
    if (!me?.banner) {
      fetchNpsBannerBlob().then((blob) => {
        if (blob?.size) {
          bannerObjectUrl = URL.createObjectURL(blob);
          setBannerUrl(bannerObjectUrl);
        }
      });
    }
    if (!me?.profile) {
      fetchNpsProfileBlob().then((blob) => {
        if (blob?.size) {
          profileObjectUrl = URL.createObjectURL(blob);
          setProfileUrl(profileObjectUrl);
        }
      });
    }
    return () => {
      if (bannerObjectUrl) URL.revokeObjectURL(bannerObjectUrl);
      if (profileObjectUrl) URL.revokeObjectURL(profileObjectUrl);
    };
  }, [me?.banner, me?.profile]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['nps', 'me'] });

  const saveAccount = async () => {
    setBusy(true);
    try {
      await updateNpsAccount({
        name,
        last_name: lastName,
        tax_identifier: stripCpfCnpj(taxId),
        legacy_user_id: passbookId.trim(),
      });
      toast.success('Dados da conta atualizados.');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar conta.');
    } finally {
      setBusy(false);
    }
  };

  const savePreferences = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('primary_color', primary);
      fd.append('secondary_color', secondary);
      await updateNpsPreferences(fd);
      toast.success('Preferências atualizadas.');
      refresh();
    } catch {
      toast.error('Falha ao salvar preferências.');
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file: File) => {
    setUploadBusy('logo');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      fd.append('primary_color', primary);
      fd.append('secondary_color', secondary);
      await updateNpsPreferences(fd);
      toast.success('Logo atualizado.');
      refresh();
    } catch {
      toast.error('Falha ao enviar logo.');
    } finally {
      setUploadBusy(null);
    }
  };

  return (
    <>
      <PageHeader title="Perfil" description="Dados da conta, identidade visual e mídia das pesquisas públicas." />

      {isLoading ? (
        <Skeleton className="h-80 max-w-4xl rounded-2xl" />
      ) : (
        <div className="max-w-4xl space-y-4">
          <Card>
            <CardHeader><CardTitle>Conta</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="size-14 text-base">
                  {profileUrl ? <AvatarImage src={profileUrl} alt="" /> : null}
                  <AvatarFallback>{initials(me?.name ?? 'NPS')}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{me?.email}</p>
                  <p className="text-sm text-muted-foreground">E-mail de acesso</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="p-name">Nome</Label>
                  <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-last">Sobrenome</Label>
                  <Input id="p-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="p-doc">CPF/CNPJ</Label>
                  <Input id="p-doc" value={taxId} onChange={(e) => setTaxId(formatCpfCnpj(e.target.value))} />
                </div>
                <div className="sm:col-span-2">
                  <PassbookIdField id="p-passbook" value={passbookId} onChange={setPassbookId} />
                </div>
              </div>
              <Button onClick={saveAccount} loading={busy}>Salvar dados da conta</Button>
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <ImageUploadCard
              title="Banner da pesquisa"
              description="Exibido no topo das pesquisas públicas."
              previewUrl={bannerUrl}
              busy={uploadBusy === 'banner'}
              onSelect={async (file) => {
                setUploadBusy('banner');
                try {
                  await uploadNpsBanner(file);
                  toast.success('Banner atualizado.');
                  refresh();
                } catch {
                  toast.error('Falha ao enviar banner.');
                } finally {
                  setUploadBusy(null);
                }
              }}
            />
            <ImageUploadCard
              title="Foto de perfil"
              description="Aparece no rodapé das pesquisas públicas."
              aspect="square"
              previewUrl={profileUrl}
              busy={uploadBusy === 'profile'}
              onSelect={async (file) => {
                setUploadBusy('profile');
                try {
                  await uploadNpsProfilePhoto(file);
                  toast.success('Foto atualizada.');
                  refresh();
                } catch {
                  toast.error('Falha ao enviar foto.');
                } finally {
                  setUploadBusy(null);
                }
              }}
            />
          </div>

          <Card>
            <CardHeader><CardTitle>Identidade visual</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <ImageUploadCard
                title="Logo da organização"
                description="Exibido no painel autenticado."
                aspect="logo"
                accept="image/png"
                previewUrl={me?.user_preference?.logo ?? null}
                busy={uploadBusy === 'logo'}
                onSelect={uploadLogo}
              />
              <div className="flex items-center justify-between">
                <Label htmlFor="p-primary">Cor primária</Label>
                <ColorPicker id="p-primary" value={primary} onChange={setPrimary} size="compact" />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="p-secondary">Cor secundária</Label>
                <ColorPicker id="p-secondary" value={secondary} onChange={setSecondary} size="compact" />
              </div>
              <div className="flex h-12 items-center justify-center rounded-xl text-sm font-medium text-white" style={{ background: `linear-gradient(120deg, ${primary}, ${secondary})` }}>
                Prévia das cores
              </div>
              <Button onClick={savePreferences} loading={busy}>Salvar cores</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
